import { Link } from 'react-router-dom';
/**
 * Charts. Hand-rolled inline SVG (D-15) — four forms at this size do not
 * justify a charting dependency.
 *
 * The categorical palette below was produced by the validator, not by eye:
 *
 *   light  #0d9488 #c2650a #2563eb #c026a3 #65a30d   on surface #fcfcfb
 *   dark   #1aa8a0 #d17a12 #4f7fe0 #c94a9e #7ba428   on surface #18191c
 *
 * Both modes pass all six checks: lightness band, chroma floor, CVD separation
 * (worst adjacent pair ΔE 11.5 protan, above the 8 target), normal-vision
 * floor, and 3:1 contrast against the surface. Dark is a separately validated
 * set of steps, not an automatic flip of the light one.
 *
 * Rules held throughout: one axis, never two. Hues assigned in fixed order and
 * never cycled. Colour follows the entity, not its rank. A legend whenever
 * there are two or more series, plus selective direct labels, so identity is
 * never carried by colour alone. Every chart has a table view.
 */
import { useId, useState, type ReactNode } from 'react';
import clsx from 'clsx';

export const CATEGORICAL = [
  'var(--c1)',
  'var(--c2)',
  'var(--c3)',
  'var(--c4)',
  'var(--c5)',
] as const;

/** Injected once; the two validated sets live here and nowhere else. */
export function ChartPalette() {
  return (
    <style>{`
      :root {
        --c1: #0d9488; --c2: #c2650a; --c3: #2563eb; --c4: #c026a3; --c5: #65a30d;
        --c-grid: rgb(var(--n3)); --c-axis: rgb(var(--n6));
      }
      :root[data-theme='dark'] {
        --c1: #1aa8a0; --c2: #d17a12; --c3: #4f7fe0; --c4: #c94a9e; --c5: #7ba428;
        --c-grid: rgb(var(--n3)); --c-axis: rgb(var(--n6));
      }
      /* Glass leads with its own orange; the rest are chosen to stay apart from it. */
      :root[data-theme='glass'] {
        --c1: #fb7e14; --c2: #6e6e6e; --c3: #c2a878; --c4: #d9626e; --c5: #6bb099;
        --c-grid: rgb(var(--n3)); --c-axis: rgb(var(--n6));
      }
    `}</style>
  );
}

function TableToggle({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <button className="h-6 rounded-xs px-1.5 text-2xs font-medium text-ink-tertiary transition-colors hover:bg-surface-hover hover:text-ink" onClick={onClick} aria-expanded={open}>
      {open ? 'Hide table' : 'Table view'}
    </button>
  );
}

/**
 * Horizontal bars — the right form for "magnitude across many named
 * categories", because a category label reads horizontally and 23 request
 * types will never fit on an x-axis.
 *
 * Single series, so one hue and no legend: the title names it.
 */
export function HBar({
  rows,
  valueLabel,
  secondary,
  max,
  color = 'var(--c1)',
  emphasise,
}: {
  /**
   * `href` makes the label a link. Optional because most bars name a
   * category with nothing behind it — a request type, an hour of the day —
   * and a link to nowhere is worse than plain text.
   */
  rows: { label: string; value: number; hint?: string; href?: string }[];
  valueLabel: (v: number) => string;
  secondary?: (row: { label: string; value: number }) => ReactNode;
  max?: number;
  color?: string;
  emphasise?: (label: string) => boolean;
}) {
  const [table, setTable] = useState(false);
  const peak = max ?? Math.max(1, ...rows.map((r) => r.value));

  if (rows.length === 0) {
    return <p className="text-ink-tertiary py-6 text-center text-xs">No data in this window.</p>;
  }

  return (
    <div>
      <div className="mb-1 flex justify-end">
        <TableToggle open={table} onClick={() => setTable(!table)} />
      </div>

      {table ? (
        <DataTable
          head={['Category', 'Value']}
          rows={rows.map((r) => [r.label, valueLabel(r.value)])}
        />
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r) => {
            const hot = emphasise?.(r.label) ?? false;
            return (
              <li key={r.label} className="group grid grid-cols-[minmax(0,11rem)_1fr_auto] items-center gap-2">
                {r.href ? (
                  <Link
                    to={r.href}
                    className={clsx(
                      'truncate text-xs underline-offset-2 hover:underline',
                      hot ? 'font-semibold text-ink' : 'text-ink-secondary hover:text-ink',
                    )}
                    title={r.hint ?? r.label}
                  >
                    {r.label}
                  </Link>
                ) : (
                  <span
                    className={clsx('truncate text-xs', hot ? 'font-semibold' : 'text-ink-secondary')}
                    title={r.label}
                  >
                    {r.label}
                  </span>
                )}
                <span className="relative h-4 rounded-sm bg-[rgb(var(--surface-sunken))]">
                  <span
                    className="absolute inset-y-0 left-0 rounded-r"
                    style={{
                      width: `${Math.max(1.5, (r.value / peak) * 100)}%`,
                      background: hot ? 'var(--c2)' : color,
                      borderRadius: '2px 4px 4px 2px',
                    }}
                    title={r.hint ?? `${r.label}: ${valueLabel(r.value)}`}
                  />
                </span>
                <span className="nums w-16 text-right text-xs font-medium">{valueLabel(r.value)}</span>
                {secondary && <span className="col-start-3 -mt-0.5">{secondary(r)}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * A stacked share bar for a categorical breakdown that sums to a whole.
 * 2px surface gaps between segments, a legend, and direct labels on the
 * segments wide enough to carry one.
 */
export function StackedShare({
  segments,
  total,
  formatValue = (v) => String(v),
}: {
  segments: { label: string; value: number }[];
  total?: number;
  formatValue?: (v: number) => string;
}) {
  const [table, setTable] = useState(false);
  const sum = total ?? segments.reduce((a, s) => a + s.value, 0);
  if (sum === 0) return <p className="text-ink-tertiary py-6 text-center text-xs">No data.</p>;

  const shown = segments.filter((s) => s.value > 0);

  return (
    <div>
      <div className="mb-2 flex justify-end">
        <TableToggle open={table} onClick={() => setTable(!table)} />
      </div>

      {table ? (
        <DataTable
          head={['Segment', 'Count', 'Share']}
          rows={shown.map((s) => [s.label, formatValue(s.value), `${((s.value / sum) * 100).toFixed(1)}%`])}
        />
      ) : (
        <>
          <div className="flex h-7 w-full gap-[2px] overflow-hidden rounded-md">
            {shown.map((s, i) => {
              const share = (s.value / sum) * 100;
              return (
                <div
                  key={s.label}
                  className="relative flex items-center justify-center overflow-hidden first:rounded-l-md last:rounded-r-md"
                  style={{ width: `${share}%`, background: CATEGORICAL[i % CATEGORICAL.length] }}
                  title={`${s.label}: ${formatValue(s.value)} (${share.toFixed(1)}%)`}
                >
                  {share > 9 && (
                    <span className="px-1 text-2xs font-semibold text-white/95">
                      {share.toFixed(0)}%
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <ul className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1">
            {shown.map((s, i) => (
              <li key={s.label} className="flex items-center gap-1.5 text-2xs">
                <span
                  aria-hidden
                  className="h-2.5 w-2.5 shrink-0 rounded-sm"
                  style={{ background: CATEGORICAL[i % CATEGORICAL.length] }}
                />
                <span className="text-ink-secondary">{s.label}</span>
                <span className="nums font-medium">{formatValue(s.value)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * Line chart with a crosshair and tooltip. 2px stroke, >=8px markers on hover
 * only, recessive grid. One y-axis — a second scale is never added.
 */
export function LineChart({
  points,
  valueLabel,
  height = 160,
  color = 'var(--c1)',
}: {
  points: { x: string; y: number }[];
  valueLabel: (v: number) => string;
  height?: number;
  color?: string;
}) {
  const clip = useId();
  const [hover, setHover] = useState<number | null>(null);
  const [table, setTable] = useState(false);

  if (points.length < 2) {
    return <p className="text-ink-tertiary py-6 text-center text-xs">Not enough points to plot.</p>;
  }

  const W = 720;
  const H = height;
  const pad = { t: 10, r: 12, b: 22, l: 40 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;

  const maxY = Math.max(...points.map((p) => p.y));
  const niceMax = niceCeil(maxY);
  const x = (i: number) => pad.l + (i / (points.length - 1)) * innerW;
  const y = (v: number) => pad.t + innerH - (v / niceMax) * innerH;

  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.y).toFixed(1)}`).join(' ');
  const area = `${path} L${x(points.length - 1).toFixed(1)},${(pad.t + innerH).toFixed(1)} L${x(0).toFixed(1)},${(pad.t + innerH).toFixed(1)} Z`;

  const ticks = [0, 0.5, 1].map((f) => Math.round(niceMax * f));

  return (
    <div>
      <div className="mb-1 flex justify-end">
        <TableToggle open={table} onClick={() => setTable(!table)} />
      </div>

      {table ? (
        <DataTable head={['Period', 'Value']} rows={points.map((p) => [p.x, valueLabel(p.y)])} />
      ) : (
        <div className="relative">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="w-full"
            style={{ height }}
            role="img"
            aria-label={`Line chart, ${points.length} points, peak ${valueLabel(maxY)}`}
            onMouseLeave={() => setHover(null)}
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const px = ((e.clientX - rect.left) / rect.width) * W;
              const i = Math.round(((px - pad.l) / innerW) * (points.length - 1));
              setHover(Math.max(0, Math.min(points.length - 1, i)));
            }}
          >
            <defs>
              <linearGradient id={clip} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity="0.16" />
                <stop offset="100%" stopColor={color} stopOpacity="0" />
              </linearGradient>
            </defs>

            {ticks.map((t) => (
              <g key={t}>
                <line
                  x1={pad.l}
                  x2={W - pad.r}
                  y1={y(t)}
                  y2={y(t)}
                  stroke="var(--c-grid)"
                  strokeWidth="1"
                />
                <text x={pad.l - 6} y={y(t) + 3.5} textAnchor="end" className="fill-current text-[9px] opacity-45">
                  {t}
                </text>
              </g>
            ))}

            <path d={area} fill={`url(#${clip})`} />
            <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

            {points.map((p, i) =>
              i === 0 || i === points.length - 1 || i % Math.ceil(points.length / 6) === 0 ? (
                <text
                  key={p.x}
                  x={x(i)}
                  y={H - 6}
                  textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}
                  className="fill-current text-[9px] opacity-45"
                >
                  {p.x}
                </text>
              ) : null,
            )}

            {hover !== null && (
              <g>
                <line
                  x1={x(hover)}
                  x2={x(hover)}
                  y1={pad.t}
                  y2={pad.t + innerH}
                  stroke="var(--c-axis)"
                  strokeWidth="1"
                  strokeDasharray="3 3"
                />
                {/* 2px surface ring so the marker reads over the line. */}
                <circle cx={x(hover)} cy={y(points[hover]!.y)} r="5" fill={color} stroke="rgb(var(--surface))" strokeWidth="2" />
              </g>
            )}
          </svg>

          {hover !== null && (
            <div
              className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-lg border bg-[rgb(var(--surface))] px-2 py-1 text-2xs shadow-pop"
              style={{ left: `${(x(hover) / W) * 100}%`, top: `${(y(points[hover]!.y) / H) * 100}%` }}
            >
              <div className="font-medium">{points[hover]!.x}</div>
              <div className="nums text-ink-secondary">{valueLabel(points[hover]!.y)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Paired bars for comparing one measure across teams — median beside p90.
 * Two series, so a legend is present and both are direct-labeled.
 */
export function PairedBars({
  rows,
  labelA,
  labelB,
  format,
}: {
  rows: { label: string; a: number | null; b: number | null }[];
  labelA: string;
  labelB: string;
  format: (v: number) => string;
}) {
  const [table, setTable] = useState(false);
  const peak = Math.max(1, ...rows.flatMap((r) => [r.a ?? 0, r.b ?? 0]));

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <ul className="flex gap-3">
          {[
            [labelA, CATEGORICAL[0]],
            [labelB, CATEGORICAL[1]],
          ].map(([l, c]) => (
            <li key={l} className="flex items-center gap-1.5 text-2xs">
              <span aria-hidden className="h-2.5 w-2.5 rounded-sm" style={{ background: c }} />
              <span className="text-ink-secondary">{l}</span>
            </li>
          ))}
        </ul>
        <TableToggle open={table} onClick={() => setTable(!table)} />
      </div>

      {table ? (
        <DataTable
          head={['Team', labelA, labelB]}
          rows={rows.map((r) => [
            r.label,
            r.a === null ? '—' : format(r.a),
            r.b === null ? '—' : format(r.b),
          ])}
        />
      ) : (
        <ul className="space-y-2.5">
          {rows.map((r) => (
            <li key={r.label}>
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <span className="truncate text-xs font-medium">{r.label}</span>
                <span className="nums text-ink-tertiary text-2xs">
                  {r.a === null ? '—' : format(r.a)} / {r.b === null ? '—' : format(r.b)}
                </span>
              </div>
              {/* 2px gap between the two fills, per the mark spec. */}
              <div className="space-y-[2px]">
                <Bar value={r.a} peak={peak} color={CATEGORICAL[0]} label={labelA} format={format} />
                <Bar value={r.b} peak={peak} color={CATEGORICAL[1]} label={labelB} format={format} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Bar({
  value,
  peak,
  color,
  label,
  format,
}: {
  value: number | null;
  peak: number;
  color: string;
  label: string;
  format: (v: number) => string;
}) {
  if (value === null) return <div className="h-2.5" />;
  return (
    <div className="h-2.5 rounded-sm bg-[rgb(var(--surface-sunken))]">
      <div
        className="h-full"
        style={{
          width: `${Math.max(1, (value / peak) * 100)}%`,
          background: color,
          borderRadius: '2px 4px 4px 2px',
        }}
        title={`${label}: ${format(value)}`}
      />
    </div>
  );
}

/** A percentage meter with the number always visible, never colour alone. */
/**
 * A day-of-week x hour-of-day grid.
 *
 * Sequential, single hue, light to dark — magnitude, not identity, so a
 * rainbow would be wrong here (and unreadable for anyone with a colour vision
 * deficiency). The scale is anchored at the busiest cell rather than at a
 * fixed maximum, because the question this answers is "when, relatively" and
 * an absolute scale would render a quiet tenant as one flat block.
 *
 * Every cell carries a title, and the table view below gives the same numbers
 * to anyone who cannot read the shading at all.
 */
export function Heatmap({
  grid,
  rowLabels,
  colLabels,
  valueLabel,
}: {
  grid: number[][];
  rowLabels: string[];
  colLabels: string[];
  valueLabel: (v: number) => string;
}) {
  const [table, setTable] = useState(false);
  const peak = Math.max(1, ...grid.flat());
  const total = grid.flat().reduce((a, b) => a + b, 0);

  if (total === 0) {
    return <p className="text-ink-tertiary py-6 text-center text-xs">No arrivals in this window.</p>;
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-3xs text-ink-tertiary">
          Shaded against the busiest hour ({valueLabel(peak)})
        </span>
        <button
          type="button"
          onClick={() => setTable((t) => !t)}
          className="text-3xs text-ink-tertiary hover:text-ink underline-offset-2 hover:underline"
        >
          {table ? 'Chart view' : 'Table view'}
        </button>
      </div>

      {table ? (
        <div className="scroll-x">
          <table className="w-full border-collapse text-2xs">
            <thead>
              <tr className="border-b text-left">
                <th scope="col" className="text-ink-secondary px-1 py-1 font-semibold">Day</th>
                {colLabels.map((c) => (
                  <th key={c} scope="col" className="text-ink-secondary px-1 py-1 text-right font-semibold">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-hair">
              {grid.map((row, i) => (
                <tr key={rowLabels[i]}>
                  <th scope="row" className="text-ink-secondary px-1 py-1 text-left font-medium">
                    {rowLabels[i]}
                  </th>
                  {row.map((v, j) => (
                    <td key={j} className="nums px-1 py-1 text-right">{v || ''}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="scroll-x">
          <div className="min-w-[34rem]">
            <div className="flex gap-[3px] pl-9">
              {colLabels.map((c, j) => (
                <span
                  key={c}
                  className="text-3xs text-ink-tertiary flex-1 text-center"
                  aria-hidden
                >
                  {j % 3 === 0 ? c : ''}
                </span>
              ))}
            </div>
            {grid.map((row, i) => (
              <div key={rowLabels[i]} className="mt-[3px] flex items-center gap-[3px]">
                <span className="text-3xs text-ink-tertiary w-9 shrink-0">{rowLabels[i]}</span>
                {row.map((v, j) => (
                  <span
                    key={j}
                    title={`${rowLabels[i]} ${colLabels[j]} — ${valueLabel(v)}`}
                    className="h-5 flex-1 rounded-[3px]"
                    style={{
                      // A floor of 0.06 keeps an empty cell visible as a cell
                      // rather than a hole in the grid.
                      backgroundColor:
                        v === 0
                          ? 'rgb(var(--n4))'
                          : `color-mix(in oklab, var(--c1) ${Math.round(12 + (v / peak) * 88)}%, rgb(var(--surface)))`,
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function Meter({
  value,
  tone = 'default',
}: {
  value: number | null;
  tone?: 'default' | 'warn' | 'danger';
}) {
  if (value === null) return <span className="text-ink-tertiary text-xs">—</span>;
  const color =
    tone === 'danger'
      ? 'rgb(var(--danger))'
      : tone === 'warn'
        ? 'rgb(var(--warn))'
        : 'var(--c1)';
  return (
    <span className="flex items-center gap-2">
      <span className="h-1.5 w-16 shrink-0 rounded-full bg-[rgb(var(--surface-sunken))]">
        <span
          className="block h-full rounded-full"
          style={{ width: `${Math.min(100, value * 100)}%`, background: color }}
        />
      </span>
      <span className="nums text-xs font-medium">{(value * 100).toFixed(0)}%</span>
    </span>
  );
}

/**
 * A plain table. Cells take a ReactNode so a row can carry a badge or an icon
 * where the value alone is not enough; numbers still get the tabular figures
 * treatment in every column but the first.
 */
export function DataTable({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <div className="scroll-x">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr className="border-b text-left">
            {head.map((h) => (
              <th key={h} scope="col" className="text-ink-secondary px-2 py-1.5 font-semibold">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-hair">
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j} className={clsx('px-2 py-1.5', j > 0 && 'nums')}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function niceCeil(v: number): number {
  if (v <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(v));
  return Math.ceil(v / mag) * mag;
}
